import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import {
  buildAnalyticsUrl,
  normalizeRoute,
  safeInteractionLabel,
  track,
  trackPageview,
} from '../lib/analytics';

const DOWNLOAD_PATTERN = /\.(pdf|xlsx?|docx?|csv|zip|rar|7z|pptx?|mp3|mp4|mov|avi)(?:$|[?#])/i;

const getArea = (element) => {
  const explicit = element.closest('[data-analytics-area]')?.dataset.analyticsArea;
  if (explicit) return explicit;
  const semantic = element.closest('nav,header,footer,form,dialog,aside,article,section,main');
  return semantic?.tagName?.toLowerCase() || 'page';
};

const classifyDestination = (href) => {
  if (!href) return undefined;
  try {
    const url = new URL(href, window.location.origin);
    if (url.origin !== window.location.origin) return `external:${url.hostname}`;
    return normalizeRoute(url.pathname);
  } catch {
    return undefined;
  }
};

const getInteractionLabel = (element, href) => {
  const explicit = element.dataset.analyticsLabel
    || element.getAttribute('aria-label')
    || element.getAttribute('title');
  if (explicit) return safeInteractionLabel(explicit);

  const destination = classifyDestination(href);
  if (destination?.includes(':id')) return undefined;
  if (element.closest('article,[data-private-content]')) return undefined;

  const isSimpleControl = element.tagName === 'BUTTON'
    || element.matches('input[type="submit"],input[type="button"]')
    || (element.tagName === 'A' && destination && !destination.includes(':id'));
  return isSimpleControl ? safeInteractionLabel(element.textContent || element.value) : undefined;
};

const AnalyticsTracker = () => {
  const location = useLocation();
  const routeKey = `${location.pathname}${location.search}`;
  const scrollMilestones = useRef(new Set());

  useEffect(() => {
    scrollMilestones.current = new Set();
    trackPageview(buildAnalyticsUrl(window.location.href), {
      route_type: normalizeRoute(location.pathname),
      has_query: Boolean(location.search),
    });

    const timers = [
      window.setTimeout(() => track('Engagement:15s'), 15000),
      window.setTimeout(() => track('Engagement:60s'), 60000),
    ];
    return () => timers.forEach((timer) => window.clearTimeout(timer));
  }, [location.pathname, location.search]);

  useEffect(() => {
    const handleClick = (event) => {
      const element = event.target?.closest?.('a,button,input[type="submit"],input[type="button"],[role="button"],[data-analytics]');
      if (!element || element.closest('[data-analytics-ignore]')) return;

      const href = element.getAttribute('href');
      let url;
      try {
        url = href ? new URL(href, window.location.origin) : null;
      } catch {
        url = null;
      }

      const props = {
        element: element.tagName.toLowerCase(),
        control_type: element.getAttribute('type') || element.getAttribute('role') || undefined,
        action: element.dataset.analyticsAction || (href ? 'navigate' : 'activate'),
        label: getInteractionLabel(element, href),
        area: getArea(element),
        destination: classifyDestination(href),
        external: Boolean(url && url.origin !== window.location.origin),
        new_window: element.getAttribute('target') === '_blank',
      };

      track(element.dataset.analyticsEvent || 'UI:Click', props);

      if (url && url.origin !== window.location.origin) {
        track('Link:Outbound', { domain: url.hostname, area: props.area });
      }
      if (href && DOWNLOAD_PATTERN.test(href)) {
        track('File:Download', { extension: href.split('.').pop()?.split(/[?#]/)[0], area: props.area });
      }
    };

    const handleSubmit = (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || form.dataset.analyticsIgnore !== undefined) return;
      track('Form:Submit', {
        form: form.dataset.analyticsForm || form.id || form.getAttribute('name') || 'unnamed',
        method: String(form.method || 'get').toLowerCase(),
        area: getArea(form),
      });
    };

    const handleCopy = (event) => {
      const element = event.target instanceof Element ? event.target : document.body;
      track('Content:Copy', { area: getArea(element) });
    };

    const handleScroll = () => {
      const scrollable = document.documentElement.scrollHeight - window.innerHeight;
      if (scrollable <= 0) return;
      const depth = Math.round((window.scrollY / scrollable) * 100);
      [25, 50, 75, 90].forEach((milestone) => {
        if (depth >= milestone && !scrollMilestones.current.has(milestone)) {
          scrollMilestones.current.add(milestone);
          track('Page:ScrollDepth', { percent: milestone });
        }
      });
    };

    const handleError = (event) => {
      track('App:Error', {
        source: event.filename ? 'script' : 'window',
        component: event.filename ? String(event.filename).split('/').pop() : undefined,
      });
    };

    const handleRejection = () => track('App:UnhandledPromise', { source: 'promise' });

    document.addEventListener('click', handleClick, true);
    document.addEventListener('submit', handleSubmit, true);
    document.addEventListener('copy', handleCopy, true);
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleRejection);

    return () => {
      document.removeEventListener('click', handleClick, true);
      document.removeEventListener('submit', handleSubmit, true);
      document.removeEventListener('copy', handleCopy, true);
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleRejection);
    };
  }, [routeKey]);

  return null;
};

export default AnalyticsTracker;
